import { EStatus, Prospect } from 'types/common';
import { TProspectListPageReq } from 'types/prospect';
import type { ProspectTagSortReq, TProspectTagAssignReq, TProspectTagListResp } from 'types/prospectTag';
import { Request } from '../request';

export const getProspectPage = (req: TProspectListPageReq) =>
    Request.post<TProspectListPageReq>({
        url: '/schedule-server/prospect/page',
        params: {
            pageNo: req.pageNo ?? 1,
            pageSize: req.pageSize ?? 50,
            email: req.email,
            name: req.name,
            phone: req.phone,
            type: req.type,
            on_boarding_status: req.on_boarding_status,
            includeSample: req.includeSample ?? false,
            tagNames: req.tagNames,
            orderKey: req.orderKey ?? 'CREATE_AT',
            orderType: req.orderType ?? 'DESC',
            status: req.status,
            contactType: req.contactType,
            // 与 practice 视图同一套筛选：两边都走服务端 eligibilityStatus，不做当前页过滤
            eligibilityStatus: req.eligibilityStatus,
        },
    });

export const getProspectList = () => Request.post({ url: '/schedule-server/prospect/list' });

export const createProspect = (item: Partial<Prospect>, overrideProviderId?: number) =>
    Request.post({ url: '/schedule-server/prospect/create', params: item, overrideProviderId });

type TSendEmailToProspect = {
    content: string;
    title: string;
    to: string;
};
export const sendEmailToProspect = (params: TSendEmailToProspect) =>
    Request.post({ url: '/schedule-server/prospect/email/reply', params });

export const getProspectNoteList = (prospectId: number) =>
    Request.post({ url: '/schedule-server/prospectNotes/listByProspectId', params: { prospectId } });

export const createProspectNote = (prospectId: number, notes: string) =>
    Request.post({ url: '/schedule-server/prospectNotes/create', params: { prospectId, notes } });

export const getProspectDetail = (prospectId: number) =>
    Request.get({ url: `/schedule-server/prospect/get/${prospectId}` });

// 把一张 PRIMARY 设为 default：后端原子地清掉同一患者其余 PRIMARY 的 default 再置位；只翻 is_default 不动 sort，
// SECONDARY 不能设，已是 default 时 no-op。GP owner 代 provider 操作走 OVERRIDE-PROVIDER-ID。返回 R<boolean>
export const setDefaultInsurance = (insuranceId: number, overrideProviderId?: number) =>
    Request.post({ url: `/schedule-server/prospect/insurance/${insuranceId}/set-default`, overrideProviderId });

export const updateProspect = (prospectId: number, item: Partial<Prospect>, overrideProviderId?: number) =>
    Request.put({ url: `/schedule-server/prospect/update/${prospectId}`, params: item, overrideProviderId });

export const getProspectAppointList = (prospectId: number) =>
    Request.get({ url: `/schedule-server/prospect/listAppointByProspectId/${prospectId}`, params: { prospectId } });

export const updateProspectEhrByIds = (prospectIds: number[], ehr: 'Yes' | 'No') =>
    Request.put({ url: '/schedule-server/prospect/updateEhr', params: { ids: prospectIds, ehr } });

export const updateProspectStatusByIds = (prospectIds: number[], status: EStatus) =>
    Request.put({ url: '/schedule-server/prospect/updateStatus', params: { ids: prospectIds, status } });

export const getProspectEmaiListByIds = (ids: string[]) =>
    Request.post({ url: '/schedule-server/prospect/email/listByIds', params: { ids } });

export const getProspectEmailIdList = (email: string) =>
    Request.post({ url: '/schedule-server/prospect/email/listId', params: { prospectEmail: email } });

export const getProspectEmailList = (email: string, prospectId: number) =>
    Request.post({ url: '/schedule-server/prospect/email/list', params: { prospectEmail: email, prospectId } });

export const batchCreateProspect = (data: Partial<Prospect>[]) =>
    Request.post({ url: 'schedule-server/prospect/create/patient/batch', params: data });

export const getProspectSettingInfo = () => Request.get({ url: '/schedule-server/provider/prospect/setting/info' });

export const getProviderNotificationConfig = () =>
    Request.get({ url: '/schedule-server/provider/getProviderNotificationConfig' });

export const saveProspectSetting = (
    avaToneName: string | null,
    avaAutoReply: boolean | null,
    _smsNotificationEnabled?: boolean,
) =>
    Request.post({
        url: '/schedule-server/provider/prospect/setting/save',
        params: { avaToneName, avaAutoReply, smsNotificationEnabled: _smsNotificationEnabled ?? null },
    });

export const saveProspectSettingSms = (smsNotificationEnabled?: boolean) =>
    Request.post({
        url: '/schedule-server/provider/prospect/setting/save',
        params: { smsNotificationEnabled, avaToneName: null, avaAutoReply: null },
    });

export const feedbackAutoReply = (emailId: string, like: boolean) =>
    Request.post({
        url: '/schedule-server/prospect/email/feedback',
        params: { emailId, like },
    });

export const initSampleProspect = () =>
    Request.put({ url: '/schedule-server/prospect/init/demo', disableErrorNote: true });

export const markSampleAppointmentGenerageFinish = () =>
    Request.put({ url: '/schedule-server/prospect/init/demoFinish', disableErrorNote: true });

export const getProspectForms = (prospectId: number) =>
    Request.get({ url: `/schedule-server/prospect/form/list/${prospectId}` });

export const downloadProspectForms = (prospectId: number, types: string) =>
    Request.get({ url: `/schedule-server/prospect/form/download/${prospectId}/${types}` });

export interface IProspectBillingInput {
    providerId: number;
    clientId: number;
    chargeType: string;
    chargeAmount: number;
    chargeReason: string;
}

export const createProspectBilling = (data: IProspectBillingInput, overrideProviderId?: number) =>
    Request.post({ url: '/schedule-server/provider/unified-billing/create', params: data, overrideProviderId });

export const getConnectAccountInfo = () =>
    Request.get({ url: '/schedule-server/provider/connect/account/info', disableErrorNote: true });

export const searchPharmacy = (pharmacyName?: string, zipCode?: string) =>
    Request.post({
        url: '/schedule-server/public/pharmacy/search',
        params: {
            zipCode,
            pharmacyName,
        },
    });

export const markPatientProfileRecordUpdatedReadByVersionId = (versionId: string) =>
    Request.post({ url: `/schedule-server/patient/profile/seen/version/update/${versionId}` });

/**
 * Add tags to a prospect incrementally (duplicates are skipped by backend).
 */
export const assignProspectTags = (req: TProspectTagAssignReq) =>
    Request.post<TProspectTagAssignReq>({
        url: '/schedule-server/prospect/tag/assign',
        params: req,
    });

/**
 * Get pre-defined candidate tags from provider_tag.
 */
export const getProspectTagCandidates = () => Request.get({ url: '/schedule-server/prospect/tag/candidates' });

/**
 * Get all distinct tag names actually used by current provider's prospects.
 */
export const getProspectTagOptions = () => Request.get({ url: '/schedule-server/prospect/tag/options' });

/**
 * Get tags for a specific prospect.
 */
export const getProspectTagList = (prospectId: number) =>
    Request.get<never>({ url: `/schedule-server/prospect/tag/list/${prospectId}` }) as Promise<{
        data?: TProspectTagListResp;
        error?: unknown;
        success?: boolean;
        errorCode?: unknown;
    }>;

/**
 * Reorder tags for a prospect.
 */
export const reorderProspectTags = (prospectId: number, items: ProspectTagSortReq[]) =>
    Request.post<ProspectTagSortReq[]>({
        url: `/schedule-server/prospect/tag/reorder/${prospectId}`,
        params: items,
    });

/**
 * Delete a tag by id.
 */
export const deleteProspectTag = (tagId: number) => Request.delete({ url: `/schedule-server/prospect/tag/${tagId}` });

// {
//     "chargeAmount": 39,
//     "chargeReason": "Text visit service fee",
//     "chargeType": "",
//     "clientId": 10001,
//     "textVisitId": 2001
//   }
//https://dev.boxpractice.com/schedule-server/doc.html#/default/provider-appointment-billing-controller/createTextVisitUsingPOST
export interface ITextVisitProspectBillingInput {
    /** Optional when chargeType is FULL_SELF_PAYMENT (amount from text visit price snapshot) */
    chargeAmount?: number;
    chargeReason: string;
    chargeType: string;
    clientId: number;
    textVisitId: string;
}

export const createProspectTextVisitBilling = (data: ITextVisitProspectBillingInput, overrideProviderId?: number) =>
    Request.post({ url: '/schedule-server/provider/billing/create-text-visit', params: data, overrideProviderId });
